import { Plus, Trash2 } from 'lucide-react';
import type { AgentApiToken } from '../../api/agentApiTokens';
import { useToast } from '../../components/ui/toastContext';
import { copyToClipboard } from './utils';
import type { Agent } from './types';

export interface ApiTokenPanelProps {
  /** 当前 Agent（展示 OpenAI 兼容模型名与调用示例）。 */
  agent: Agent | null;
  tokens: AgentApiToken[];
  loading: boolean;
  /** 生成表单是否展开（受控于页面）。 */
  formOpen: boolean;
  /** 打开生成表单（页面同步重置名称与 secret）。 */
  onOpenForm: () => void;
  /** 取消生成表单。 */
  onCancelForm: () => void;
  newTokenName: string;
  onNewTokenNameChange: (name: string) => void;
  /** 新创建 Token 的完整 secret（仅创建时可见一次）。 */
  newTokenSecret: string | null;
  onCreate: () => void;
  onToggle: (token: AgentApiToken) => void;
  onDelete: (token: AgentApiToken) => void;
}

/**
 * API Token 维度面板：Token 生成 / 列表 / 启停 / 删除，以及 OpenAI 兼容调用说明。
 * 数据与操作均受控于页面（保持跨维度切换时的状态不丢失）。
 */
export default function ApiTokenPanel({
  agent,
  tokens,
  loading,
  formOpen,
  onOpenForm,
  onCancelForm,
  newTokenName,
  onNewTokenNameChange,
  newTokenSecret,
  onCreate,
  onToggle,
  onDelete,
}: ApiTokenPanelProps) {
  const toast = useToast();
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 style={{ margin: 0 }}>API Token 管理</h3>
        <button
          className="primary"
          style={{ padding: '6px 12px', fontSize: 13 }}
          onClick={onOpenForm}
        >
          <Plus size={14} /> 生成 Token
        </button>
      </div>
      {formOpen && (
        <div className="card mb-3" style={{ background: 'var(--bg-primary)' }}>
          <h4 className="mb-2">生成新 Token</h4>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              placeholder="Token 名称（例如：production）"
              value={newTokenName}
              onChange={(e) => onNewTokenNameChange(e.target.value)}
              style={{ flex: 1 }}
            />
            <button className="primary" onClick={onCreate} disabled={!newTokenName.trim()}>
              生成
            </button>
            <button className="secondary" onClick={onCancelForm}>
              取消
            </button>
          </div>
          {newTokenSecret && (
            <div className="p-2" style={{ background: 'rgba(126,231,135,0.12)', borderRadius: 6, color: 'var(--color-success)' }}>
              <strong>请立即复制，关闭后将无法再次查看：</strong>
              <div className="flex items-center gap-2 mt-1">
                <code style={{ wordBreak: 'break-all' }}>{newTokenSecret}</code>
                <button
                  className="secondary"
                  style={{ padding: '4px 8px', fontSize: 12, whiteSpace: 'nowrap' }}
                  onClick={async () => {
                    const ok = await copyToClipboard(newTokenSecret);
                    if (ok) {
                      toast.success('已复制');
                    } else {
                      toast.error('复制失败');
                    }
                  }}
                >
                  复制
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {loading ? (
        <p className="text-muted">加载中...</p>
      ) : tokens.length === 0 ? (
        <p className="text-muted">暂无 API Token，点击右上角生成</p>
      ) : (
        <div className="list">
          {tokens.map((token) => (
            <div key={token.id} className="list-item">
              <div className="list-item-content">
                <strong>{token.name || '未命名 Token'}</strong>
                <div className="text-muted" style={{ fontSize: 13, marginTop: 4 }}>
                  Token: <code>{token.token_prefix || 'at_****'}</code>
                  {' · '}
                  状态: {token.is_active ? '启用' : '禁用'}
                  {token.last_used_at && ` · 最近使用: ${new Date(token.last_used_at).toLocaleString()}`}
                  {token.expires_at && ` · 过期: ${new Date(token.expires_at).toLocaleString()}`}
                  <span style={{ fontSize: 11, marginLeft: 8, color: 'var(--text-muted)' }}>
                    （仅创建时可查看完整 Token）
                  </span>
                </div>
              </div>
              <div className="list-item-actions">
                <button
                  className={token.is_active ? 'danger' : 'primary'}
                  style={{ padding: '6px 10px', fontSize: 12 }}
                  onClick={() => onToggle(token)}
                >
                  {token.is_active ? '禁用' : '启用'}
                </button>
                <button
                  className="danger"
                  style={{ padding: '6px 10px' }}
                  onClick={() => onDelete(token)}
                  title="删除"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="card mt-4" style={{ background: 'var(--bg-primary)' }}>
        <h4 className="mb-2">OpenAI 兼容模型名</h4>
        <p className="text-muted" style={{ fontSize: 13, marginBottom: 12 }}>
          调用 /v1/chat/completions 时，<code>model</code> 字段必须填下面任意一个：
        </p>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between" style={{ background: 'var(--bg-tertiary)', padding: '8px 12px', borderRadius: 6 }}>
            <code style={{ fontSize: 13 }}>{agent?.id}</code>
            <button
              className="secondary"
              style={{ padding: '4px 8px', fontSize: 12 }}
              onClick={async () => {
                const ok = await copyToClipboard(agent?.id || '');
                if (ok) {
                  toast.success('Agent ID 已复制');
                } else {
                  toast.error('复制失败');
                }
              }}
            >
              复制 Agent ID
            </button>
          </div>
          <div className="flex items-center justify-between" style={{ background: 'var(--bg-tertiary)', padding: '8px 12px', borderRadius: 6 }}>
            <code style={{ fontSize: 13 }}>{agent?.name?.toLowerCase().replace(/\s+/g, '_') || 'demo_agent'}</code>
            <button
              className="secondary"
              style={{ padding: '4px 8px', fontSize: 12 }}
              onClick={async () => {
                const modelName = agent?.name?.toLowerCase().replace(/\s+/g, '_') || '';
                const ok = await copyToClipboard(modelName);
                if (ok) {
                  toast.success('模型名已复制');
                } else {
                  toast.error('复制失败');
                }
              }}
            >
              复制模型名
            </button>
          </div>
        </div>
      </div>

      <div className="card mt-4" style={{ background: 'var(--bg-primary)' }}>
        <h4 className="mb-2">调用示例</h4>
        <pre style={{ fontSize: 12, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
{`curl -H "Authorization: Bearer <your-token>" \\
  -H "Content-Type: application/json" \\
  -d '{"model":"${agent?.name?.toLowerCase().replace(/\\s+/g, '_') || '<agent_name>'}","messages":[{"role":"user","content":"你好"}]}' \\
  http://${window.location.host}/api/v1/openai/v1/chat/completions`}
        </pre>
      </div>
    </div>
  );
}
